<template>
  <div id="audit-logs-table" class="page-table scrollable only-y">
    <el-dialog
      width="440px"
      :visible="modalVisible"
      :before-close="handleCloseModal"
      :close-on-click-modal="false"
    >
      <h2 class="form-section-header">Filter Settings</h2>

      <h3 class="black-text mb-5">Select Date Range</h3>
      <el-date-picker
        class="mb-10"
        style="margin-left: auto"
        v-model="historyDateRange"
        type="datetimerange"
        align="right"
        unlink-panels
        format="yyyy-MM-dd HH:mm"
        :clearable="false"
        range-separator="To"
        start-placeholder="Start date"
        end-placeholder="End date"
        :picker-options="datePickerOptions"
      >
      </el-date-picker>

      <el-form id="stats-advanced-modal" class="form-modal">
        <h3 class="black-text mb-10">Select Actions</h3>
        <el-checkbox-group v-model="actions">
          <el-checkbox label="Create">Create</el-checkbox>
          <el-checkbox label="Update">Update</el-checkbox>
          <el-checkbox label="Delete">Delete</el-checkbox>
        </el-checkbox-group>

        <h3 class="black-text mb-0">Filter By</h3>
        <el-select
          v-model="primaryFilter"
          placeholder="Select Filter"
          clearable
          style="margin-top: 0"
        >
          <template slot="prefix"> Filter By </template>
          <el-option value="service" label="Service" />
          <el-option value="user" label="User" />
        </el-select>

        <dads-select
          v-if="primaryFilter === 'user'"
          v-model="userId"
          placeholder="Search user"
          remote
          allow-create
          filterable
          clearable
          :remote-method="searchUsers"
        >
          <template slot="prefix">User</template>
          <el-option
            v-for="item in userOptions"
            :key="item.key"
            :label="item.name"
            :value="item.id"
          />
        </dads-select>

        <dads-select
          v-if="primaryFilter === 'service'"
          v-model="service"
          placeholder="Select service"
          filterable
          clearable
        >
          <template slot="prefix">Service</template>
          <el-option
            v-for="(item, index) in serviceOptions"
            :key="index"
            :label="getServiceName(item)"
            :value="item"
          />
        </dads-select>

        <el-button class="mt-20" @click="doAdvanced"> Update </el-button>
      </el-form>
    </el-dialog>

    <div class="page-header no-table-box">
      <el-row class="hidden-sm-and-up pb-15">
        <h1>
          <v-svg class="voxo-orange" sprite="list" />
          Audit Logs
        </h1>
      </el-row>
      <el-row class="hidden-xs-only pb-15">
        <el-col :span="16">
          <h1>
            <v-svg class="voxo-orange" sprite="list" />
            Audit Logs
          </h1>
        </el-col>

        <el-col class="text-right" :span="8">
          <el-tag>
            <i class="el-icon-info" style="margin-right: 4px" />
            Total: {{ total }}
          </el-tag>
        </el-col>
      </el-row>

      <div class="flex align-center mb-20">
        <el-radio-group
          v-model="presetSelection"
          @change="doPreset"
          class="hidden-xs-only"
        >
          <el-radio-button type="default" label="last10"
            >Last 10 Days</el-radio-button
          >
          <el-radio-button label="lastMonth">Last 30 Days</el-radio-button>
        </el-radio-group>

        <el-tooltip placement="top" :content="selectedDateRange">
          <el-button @click="modalVisible = true" class="ml-10">
            Filter
          </el-button>
        </el-tooltip>
        <el-button
          @click="resetTable"
          v-if="advancedActive"
          style="background-color: #f25a6b; color: #fff"
        >
          Reset
        </el-button>
        <el-button class="ml-10" @click="doExport"> Export </el-button>
        <div class="box right">
          <el-pagination
            v-if="total"
            small
            background
            layout="prev, pager, next"
            @current-change="pageSet"
            @next-click="pageNext"
            @prev-click="pagePrevious"
            :current-page="page"
            :page-size="limit"
            :pager-count="5"
            :total="total"
          />
        </div>
      </div>

      <div class="vxe-table-height card-base card-shadow--medium">
        <vxe-grid
          round
          border="inner"
          ref="xGrid"
          height="100%"
          auto-resize
          empty-text="No Data"
          show-overflow="tooltip"
          highlight-hover-row
          :data="tableData"
          :scroll-y="{ enabled: true, gt: 50, oSize: 0 }"
          :loading="tableLoading"
          :columns="tableColumns"
        />
      </div>
    </div>
  </div>
</template>

<script>
import feathersClient from "../../../feathers/feathers-client";
import moment from "moment-timezone";
import { debounce } from "lodash";
import DadsSelect from "@/components/select";
import serviceMapper from "./modules/service-mapper";
import { ExportToCsv } from "export-to-csv";

export default {
  name: "AdminAuditLog",
  components: { DadsSelect },
  data() {
    return {
      total: 0,
      page: 1,
      pages: 1,
      limit: 150,
      presetSelection: "init",
      advancedActive: false,
      modalVisible: false,
      tableData: [],
      tableLoading: false,
      start: null,
      end: null,
      primaryFilter: "",
      service: "",
      userId: "",
      actions: ["Create", "Update", "Delete"],
      serviceOptions: Object.keys(serviceMapper),
      exportCSV: false,
      userOptions: [],
      historyDateRange: [moment(), moment()],
      datePickerOptions: {
        shortcuts: [
          {
            text: "Last week",
            onClick(picker) {
              const end = moment(moment().format("YYYY-MM-DD"))
                .add(1, "day")
                .subtract(1, "minute");
              const start = moment(
                moment().subtract(1, "week").format("YYYY-MM-DD")
              );
              picker.$emit("pick", [start, end]);
            },
          },
          {
            text: "Last month",
            onClick(picker) {
              const end = moment(moment().format("YYYY-MM-DD"))
                .add(1, "day")
                .subtract(1, "minute");
              const start = moment(
                moment().subtract(1, "month").format("YYYY-MM-DD")
              );
              picker.$emit("pick", [start, end]);
            },
          },
          {
            text: "Last 3 months",
            onClick(picker) {
              const end = moment(moment().format("YYYY-MM-DD"))
                .add(1, "day")
                .subtract(1, "minute");
              const start = moment(
                moment().subtract(3, "months").format("YYYY-MM-DD")
              );
              picker.$emit("pick", [start, end]);
            },
          },
          {
            text: "Last 6 months",
            onClick(picker) {
              const end = moment(moment().format("YYYY-MM-DD"))
                .add(1, "day")
                .subtract(1, "minute");
              const start = moment(
                moment().subtract(6, "months").format("YYYY-MM-DD")
              );
              picker.$emit("pick", [start, end]);
            },
          },
          {
            text: "Last year",
            onClick(picker) {
              const end = moment(moment().format("YYYY-MM-DD"))
                .add(1, "day")
                .subtract(1, "minute");
              const start = moment(
                moment().subtract(12, "months").format("YYYY-MM-DD")
              );
              picker.$emit("pick", [start, end]);
            },
          },
        ],
      },
      tableColumns: [
        {
          field: "time",
          title: "Time",
          minWidth: 175,
          sortable: true,
          slots: {
            default: ({ row }) => {
              return [<span>{this.timeFormatter(row.time)}</span>];
            },
          },
        },
        {
          field: "userEmail",
          title: "Email",
          minWidth: 200,
          sortable: true,
          showOverflow: true,
        },
        {
          field: "service",
          title: "Service",
          minWidth: 150,
          sortable: true,
        },
        {
          field: "action",
          title: "Action",
          minWidth: 100,
          sortable: true,
        },
        {
          field: "recordDescription",
          title: "Description",
          minWidth: 225,
          sortable: true,
          showOverflow: true,
        },
      ],
    };
  },
  computed: {
    selectedDateRange() {
      if (this.end && this.start) {
        const dateStart = moment(this.start).format("M/D/YY HH:mm");
        const dateEnd = moment(this.end).format("M/D/YY HH:mm");
        return `${dateStart} - ${dateEnd}`;
      }
    },
    selectedTenant() {
      return this.$store.state.selectedTenant;
    },
    user() {
      return this.$store.state.auth.user;
    },
  },
  watch: {
    selectedTenant() {
      this.resetTable();
    },
    page: debounce(function () {
      this.queryRecords();
    }, 500),
  },
  methods: {
    doPreset(val) {
      if (val === "init") {
        this.end = moment(moment().format("YYYY-MM-DD"))
          .add(1, "day")
          .subtract(1, "minute")
          .format("YYYY-MM-DD HH:mm");
        this.start = moment().startOf("day").format("YYYY-MM-DD");
        this.presetSelection = "init";
        this.updateDateRangePicker();
        this.queryRecords();
      }

      if (val === "last10") {
        this.end = moment(moment().format("YYYY-MM-DD"))
          .add(1, "day")
          .subtract(1, "minute")
          .format("YYYY-MM-DD HH:mm");
        this.start = moment().subtract(9, "days").format("YYYY-MM-DD");
        this.presetSelection = "last10";
        this.updateDateRangePicker();
        this.queryRecords();
      }

      if (val === "lastMonth") {
        this.end = moment(moment().format("YYYY-MM-DD"))
          .add(1, "day")
          .subtract(1, "minute")
          .format("YYYY-MM-DD HH:mm");
        this.start = moment().subtract(30, "days").format("YYYY-MM-DD");
        this.presetSelection = "lastMonth";
        this.updateDateRangePicker();
        this.queryRecords();
      }
    },

    doAdvanced() {
      this.advancedActive = true;
      this.start = moment(this.historyDateRange[0]).format(
        "YYYY-MM-DD HH:mm:ss"
      );
      this.end = moment(this.historyDateRange[1]).format("YYYY-MM-DD HH:mm:ss");
      this.updateDateRangePicker();
      this.queryRecords();
      this.handleCloseModal();
      this.presetSelection = null;
    },

    doExport() {
      this.$confirm(`Export data for ${this.total} records?`, {
        type: "warning",
      })
        .then(() => {
          if (this.pages === 1) {
            //export from browser
            let csvData = [];
            for (let i = 0; i < this.tableData.length; i++) {
              const csvObject = {};
              let exclusionList = ["_XID"];
              for (let key in this.tableData[i]) {
                if (exclusionList.includes(key) === false) {
                  csvObject[key] = this.tableData[i][key]
                    ? this.tableData[i][key]
                    : "";
                }
              }
              csvData.push(csvObject);
            }

            const options = {
              filename: "audit_log_records",
              showLabels: true,
              headers: ["Time", "Email", "Service", "Action", "Description"],
            };

            let csvReorder = csvData.map((obj) => {
              let reorderObj = {};
              reorderObj["time"] = this.timeFormatterExport(obj.time);
              reorderObj["email"] = obj.userEmail;
              reorderObj["service"] = obj.service;
              reorderObj["action"] = obj.action;
              reorderObj["description"] = obj.recordDescription;
              return reorderObj;
            });

            csvData.push(csvReorder);
            const csvExporter = new ExportToCsv(options);
            csvExporter.generateCsv(csvReorder);
          } else {
            this.exportCSV = true;
            this.queryRecords();
          }
        })
        .catch(() => true);
    },

    resetTable() {
      this.advancedActive = false;
      this.primaryFilter = "";
      this.service = "";
      this.actions = ["Create", "Update", "Delete"];
      this.userId = "";
      this.exportCSV = false;
      this.page = 1;
      this.userOptions = [];
      this.doPreset("init");
    },

    updateDateRangePicker() {
      this.historyDateRange = [moment(this.start), moment(this.end)];
    },

    queryRecords() {
      this.tableLoading = true;
      feathersClient
        .service("audit-logs")
        .find({
          query: {
            start: this.start,
            end: this.end,
            primaryFilter: this.primaryFilter,
            actions: this.actions,
            service: this.service,
            userId: this.userId,
            tenantId: this.selectedTenant.id,
            $limit: this.limit,
            $skip: this.page > 1 ? this.limit * (this.page - 1) : 0,
            timezone: this.user.timezone,
            exportCSV: this.exportCSV,
          },
        })
        .then((res) => {
          if (!this.exportCSV) {
            this.limit = res.limit;
            this.pages = Math.ceil(res.total / res.limit);
            this.total = res.total;
            this.page = this.page > this.pages ? 1 : this.page;
            this.tableData = res.data;
          }

          if (this.exportCSV) {
            if (!res.url) {
              this.$notify({
                title: "Not Found",
                message: "File Not Found.",
                type: "warning",
              });
            } else {
              this.downloadCSV(res);
            }
          }

          this.exportCSV = false;
          this.tableLoading = false;
        })
        .catch((res) => {
          this.tableLoading = false;
        });
    },

    searchUsers(query) {
      if (query.length) {
        const searchTerm = (query + "")
          .replace(/[\\"']/g, "\\$&")
          .replace(/\u0000/g, "\\0");

        feathersClient
          .service("users")
          .find({
            query: {
              tenantId: this.selectedTenant.id,
              email: { $like: `%${searchTerm}%` },
              $limit: 25,
            },
          })
          .then((res) => {
            this.userOptions = res.map((item) => {
              return {
                key: this.getRandomKey(),
                name: item.email,
                id: item.id,
              };
            });
          });
      }
    },

    downloadCSV(data) {
      const { url, downloadName } = data;
      const link = document.createElement("a");
      link.href = url;
      link.download = downloadName;
      link.click();
    },

    //General Form and Table Operations
    pageNext() {
      if (this.page < this.pages) {
        this.page += 1;
      }
    },

    pagePrevious() {
      if (this.page > 1) {
        this.page -= 1;
      }
    },

    pageSet(page) {
      this.page = page;
    },

    handleCloseModal() {
      this.modalVisible = false;
    },

    getRandomKey() {
      const randomString = () => Math.random().toString(36).slice(2);
      return `${randomString()}-${randomString()}$${randomString()}`;
    },

    getServiceName(val) {
      return serviceMapper[val].name;
    },

    timeFormatter(val) {
      return val ? moment(val).format("M/DD/YYYY h:mm:ss A") : val;
    },

    timeFormatterExport(value) {
      const offsetMinutes = Math.abs(this.userOffsetMinutes);

      return this.userOffsetMinutes < 0
        ? moment(value)
            .subtract(offsetMinutes, "minutes")
            .format("YYYY-MM-DD hh:mm:ss A")
        : moment(value)
            .add(offsetMinutes, "minutes")
            .format("YYYY-MM-DD hh:mm:ss A");
    },
  },
  created() {
    this.doPreset("init");
  },
};
</script>

<style lang="scss" scoped>
.el-checkbox {
  margin-left: 0;
}
</style>
